import { useMemo, useRef } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import {
  PageViewedTracker,
  useButtonPressedCallback
} from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { WebView } from '@zenbusiness/application-commons-webview';
import ZuiBanner from '@zenbusiness/zui/Banner';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Stack from '@zenbusiness/zui/Stack';
import { useLocation } from 'react-router-dom';

import { PartnerFootnotes } from '#client/components/BankingOptions';
import { Breadcrumbs } from '#client/components/Breadcrumbs';
import { DisableLayer } from '#client/components/DisableLayer';
import Footer from '#client/components/Experimentation/components/Footer';
import { FocusGrid, HubContainerGridStyled, HubMainWrapperStyled } from '#client/components/Grid';
import Notifications from '#client/components/Notifications';
import {
  RELEASE_BANKING_UPSELL_PAGE_AND_NON_QUALIFIED_PAGE_TO_INCLUDE_BLUEVINE as OFFER_FLAG,
  ZB_BANKING_SUNSET_PH3
} from '#client/constants/experimentsFlags';
import { BANKING_ELIGIBILITY_STATUS } from '#client/constants/validationMessages';
import useBankingEligibilityCheck from '#client/hooks/useBankingEligibilityCheck';
import useFeatureFlag from '#client/hooks/useFeatureFlag';

import { BreadcrumbWrapStyled } from '../SharedStyles';
import { Banner } from './components/Banner';
import { Benefits } from './components/Benefits';
import { HowToUse } from './components/HowToUse';
import { Offers } from './components/Offers';
import { PartnerOffers } from './components/PartnerOffers';
import { Quote } from './components/Quote';

import { BANKING_LANDING_OPTION_EVENTS, BANKING_LANDING_PAGE_NAME } from './constants';

const { isWebView } = WebView;

export default function BusinessBankingAccounts() {
  const { pathname } = useLocation();
  const { bankingEligibilityStatus, isCheckingEligibleBankingApplication, isEligibleForBanking } =
    useBankingEligibilityCheck({});

  const isFormationOrderIncomplete =
    bankingEligibilityStatus === BANKING_ELIGIBILITY_STATUS.FORMATION_ORDER_INCOMPLETE &&
    !isCheckingEligibleBankingApplication;

  const {
    businessEntityUuid,
    businessEntity: { type }
  } = useBusinessEntity();

  const goToFormation = `/f/go/complete_${type.toLowerCase()}_formation?businessEntityUuid=${businessEntityUuid}`;
  const offerVariation = useExperimentFlag(OFFER_FLAG.FLAG, OFFER_FLAG.VARIATIONS.CONTROL);
  // ZB Banking is closing, so the page sells the partner offers instead of our own account.
  const [isBankingSunsetPh3, isLoadingFlag] = useFeatureFlag(ZB_BANKING_SUNSET_PH3);
  const targetRef = useRef(null);

  const properties = useMemo(() => {
    const pageName = (() => {
      switch (offerVariation) {
        case OFFER_FLAG.VARIATIONS.VERSION_A:
          return 'category_selling_banking_landing_withchase';
        case OFFER_FLAG.VARIATIONS.VERSION_B:
          return 'category_selling_banking_landing_withoutchase';
        case OFFER_FLAG.VARIATIONS.VERSION_C:
          return 'category_selling_banking_landing_withoutBoA';
        default:
          return BANKING_LANDING_PAGE_NAME;
      }
    })();

    return {
      customDestinationPageName_: pageName,
      appPageName: pageName
    };
  }, [offerVariation]);

  const trackingButtonPressed = useButtonPressedCallback({
    elementText: 'View Banking options',
    elementName: 'view_banking_options_catbankupsell',
    appPageName: BANKING_LANDING_PAGE_NAME
  });

  const handleScroll = () => {
    trackingButtonPressed();
    if (targetRef.current) {
      const headerHeight = 80;
      const elementPosition = targetRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Hold the page rather than flash the ZenBusiness offers we are about to stop selling.
  if (isLoadingFlag) {
    return <PageLoading />;
  }

  return (
    <HubContainerGridStyled>
      <FocusGrid>
        <HubMainWrapperStyled>
          {!isWebView && (
            <BreadcrumbWrapStyled>
              <Breadcrumbs
                items={[
                  {
                    to: '../../finances',
                    label: 'Finances'
                  },
                  {
                    label: 'Business Banking Accounts'
                  }
                ]}
              />
            </BreadcrumbWrapStyled>
          )}
          {isFormationOrderIncomplete && (
            <ZuiBanner
              slotProps={{
                primaryAction: {
                  label: 'Finish Formation',
                  href: goToFormation
                }
              }}
              subtitle="Before we file to get your business official and open a banking account, you need to give us a bit more info."
              sx={{ mb: '2rem' }}
              title="Finish your formation"
              variant="info"
            />
          )}
          <Notifications />
          <PageViewedTracker
            {...properties}
            key={pathname}
          />
          <Box sx={{ position: 'relative' }}>
            {/* The formation gate guards buying the ZenBusiness account, not the partner referrals. */}
            {isFormationOrderIncomplete && !isBankingSunsetPh3 && (
              <DisableLayer data-testid="disable-layer" />
            )}
            <Stack spacing={6}>
              <Banner handleScroll={handleScroll} />
              {isBankingSunsetPh3 ? (
                <PartnerOffers ref={targetRef} />
              ) : (
                <>
                  <Quote />
                  <Offers ref={targetRef} />
                </>
              )}
              <Benefits />
              <Divider />
              {isBankingSunsetPh3 ? (
                <PartnerFootnotes
                  appPageName={BANKING_LANDING_OPTION_EVENTS.appPageName}
                  elementName={BANKING_LANDING_OPTION_EVENTS.elementNames.mercuryAgreementLink}
                />
              ) : (
                <>
                  {isEligibleForBanking && <HowToUse />}
                  <Footer />
                </>
              )}
            </Stack>
          </Box>
        </HubMainWrapperStyled>
      </FocusGrid>
    </HubContainerGridStyled>
  );
}
